import axios, { AxiosAdapter, AxiosPromise, AxiosRequestConfig } from 'axios';
import LRUCache from 'lru-cache';
import { UserModule } from '@/store/modules/user';
import { AppModule } from '@/store/modules/app';
import { clearUserRelatedStates } from './app';
import MainApp from '@/main';
import {
  PermissionModule,
  UserTokenPermission,
} from '@/store/modules/permission';

// Axios cache adapter
const FIVE_MINUTES = 1000 * 60 * 5;
const CAPACITY = 100;
const logForCache =
  process.env.NODE_ENV !== 'production' && process.env.NO_CACHE_LOG !== 'true';
const isRequestPathCachable = (path: string) =>
  !!path && (path.startsWith('/v1/list') || path.startsWith('/v1/search')); // only cache '/v1/list' or '/v1/search' by default

export interface ICacheLike<T> {
  get(key: string): T | undefined;

  set(key: string, value: T, maxAge?: number): boolean;

  del(key: string): void;
}

export const isCacheLike = (cache: any) => {
  return !!(
    cache.set &&
    cache.get &&
    cache.del &&
    typeof cache.get === 'function' &&
    typeof cache.set === 'function' &&
    typeof cache.del === 'function'
  );
};

export type RequestCacheOptions = {
  enabledByDefault?: boolean;
  cacheFlag?: string;
  defaultCache?: ICacheLike<AxiosPromise>;
};

const newAxiosCacheAdpater = (
  adapter: AxiosAdapter,
  options: RequestCacheOptions = {}
) => {
  const {
    enabledByDefault = true,
    cacheFlag = 'cache',
    defaultCache = new LRUCache<string, AxiosPromise>({
      maxAge: FIVE_MINUTES,
      max: CAPACITY,
    }),
  } = options;

  return (config: AxiosRequestConfig) => {
    const { baseURL, url, data } = config;
    const requestPath = url!.substr(baseURL!.length - 1);
    const useCache =
      (config as any)[cacheFlag] !== void 0 &&
      (config as any)[cacheFlag] !== null
        ? (config as any)[cacheFlag]
        : isRequestPathCachable(requestPath!) && enabledByDefault;

    if (useCache) {
      // if had provide a specified cache, then use it instead
      const cache: ICacheLike<AxiosPromise> = isCacheLike(useCache)
        ? useCache
        : defaultCache;

      // build the index according to the url and data
      const index = `url:${url}#:#data:${JSON.stringify(data)}`;

      let responsePromise = cache.get(index);

      if (!responsePromise) {
        responsePromise = (async () => {
          try {
            return await adapter(config);
          } catch (reason) {
            cache.del(index);
            throw reason;
          }
        })();

        // put the promise for the non-transformed response into cache as a placeholder
        cache.set(index, responsePromise);

        return responsePromise;
      }

      if (logForCache) {
        // eslint-disable-next-line no-console
        console.info(`request cached by cache adapter: ${index}`);
      }

      return responsePromise;
    }

    return adapter(config);
  };
};

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  timeout: 30000,
  // adapter: newAxiosCacheAdpater(axios.defaults.adapter!), // enable this to use request cache
  // withCredentials: true // send cookies when cross-domain requests
});

// Request interceptors
service.interceptors.request.use(
  config => {
    if (UserModule.token && UserModule.tokenValue) {
      config.headers['grpc-metadata-auth-token'] = UserModule.tokenValue;
    }
    return config;
  },
  error => {
    Promise.reject(error);
  }
);

const logout = async () => {
  AppModule.StartProgress();
  try {
    await UserModule.Logout();
    clearUserRelatedStates();
    PermissionModule.SetUserTokenPermission(
      new UserTokenPermission(false, false, true)
    );
    MainApp.$router.push(`/login?redirect=${MainApp.$route.fullPath}`);
  } catch (err) {
    AppModule.StopProgress();
    MainApp.$toast.error(`logout: ${err}`, {
      icon: 'mdi-account-alert',
    });
  }
};

const logOnError = process.env.NODE_ENV !== 'production';

// Response interceptors
service.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (logOnError) {
      const log =
        error.response && error.response.data ? error.response.data : error;
      // eslint-disable-next-line no-console
      console.log(`response error: ${JSON.stringify(log)}`);
    }

    if (
      error.response.status === 401 &&
      !(
        error.config &&
        error.config.url &&
        (error.config.url as string).includes('logout')
      )
    ) {
      logout();
    }

    return Promise.reject(error.response);
  }
);

export default service;
